'use client';

import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useUploadHistoricFileMutation } from '@/app/services/sharingResultApi';
import DrawerButtons from '@/components/common/DrawerButtons';
import ModalPageTitle from '@/components/common/ModalPageTitle';
import { Box, Button, TextField, Typography, useTheme } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateField } from '@mui/x-date-pickers/DateField';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

interface UploadFileHistoricProps {
  setDrawerOpen: (open: boolean) => void;
}

export default function UploadHistoricFile(props: UploadFileHistoricProps) {
  const [period, setPeriod] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>();
  const [validDate, setValidDate] = useState(false);
  const theme = useTheme();

  const { setDrawerOpen } = props;
  const { t } = useTranslation(['historic', 'common']);

  const [uploadFile] = useUploadHistoricFileMutation();

  const handleSubmit = async () => {
    let response = {};
    const formData = new FormData();
    formData.append('file', selectedFile || '');
    formData.append('period', period as string);

    setDrawerOpen(false);
    toast.info(
      t('common:processing.wait.a.moment', {
        position: toast.POSITION.TOP_RIGHT,
      }),
    );
    response = await uploadFile(formData);
    if ('data' in response) {
      toast.dismiss();
      toast.success(
        t('historic.processing.success', {
          position: toast.POSITION.TOP_RIGHT,
        }),
      );
    } else {
      toast.error(
        t('common:processing.failed', {
          position: toast.POSITION.TOP_RIGHT,
        }),
      );
    }
  };

  const handleCancel = () => {
    setDrawerOpen(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 24px)',
        maxHeight: 'calc(100vh - 24px)',
        flexGrow: 'inherit',
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <ModalPageTitle title={t('title.upload.historic')} subtitle={t('upload.historic.page.message') as string} />

        <Box
          sx={{
            display: 'block',
            flexDirection: 'row',
            mt: 4,
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'block', minWidth: theme.spacing(31), mb: 4 }}>
            <Typography component="p" variant="fieldHeader" sx={{ mt: 2 }}>
              {t('common:period')}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateField
                variant="outlined"
                size="small"
                value={period}
                format="MM-YYYY"
                onChange={(newValue, context) => {
                  setValidDate(false);
                  if (context.validationError !== null) {
                    return;
                  }
                  const date = newValue ? dayjs(newValue).startOf('M').format('YYYY-MM-DD') : null;
                  setPeriod(date);
                  setValidDate(true);
                }}
              />
            </LocalizationProvider>
          </Box>

          <Box sx={{ flex: 1, minWidth: theme.spacing(31) }}>
            <Typography component="p" variant="fieldHeader" sx={{ mt: 2 }}>
              {t('select.excel.file')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
              <TextField
                size="small"
                disabled
                value={selectedFile ? selectedFile.name : ''}
                placeholder={t('no.file.chosen') as string}
                sx={{
                  flexGrow: 1,
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: 'text.primary',
                  },
                }}
              />
              <Button
                disabled={!validDate}
                variant="contained"
                component="label"
                sx={{
                  minWidth: '40%',
                  backgroundColor: !period ? 'action.disabled' : 'primary.main',
                  color: !period ? 'text.disabled' : 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: !period ? 'action.disabled' : 'primary.dark',
                  },
                }}
              >
                <Typography sx={{ fontSize: 13 }}>{t('common:choose.file')}</Typography>
                <input
                  type="file"
                  onChange={(e) => {
                    if (e.target.files) {
                      setSelectedFile(((e.target as HTMLInputElement).files || [null])[0]);
                    }
                  }}
                  hidden
                  multiple={false}
                />
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 'auto' }}>
        <DrawerButtons onSave={handleSubmit} onCancel={handleCancel} disabled={!validDate || !selectedFile} />
      </Box>
    </Box>
  );
}
